import { Icon } from "./icon";
import { cx } from "@/utils/cx";
import type { IconName } from "./icon";
import type { ReactNode } from "react";

/**
 * Gymkee 2027 inline alert (spec inline-alert 1.7; sheet DS08).
 *
 * A soft-tint box at radius md with the mark aligned to the first line of the text. No border,
 * no rail down the left edge, no orange anywhere in it, and never more than one action, which is
 * tertiary and never a primary. The sentence carries the meaning; the colour only confirms it.
 *
 * A danger alert is announced once, assertively: a payment that failed is the one thing on this
 * page a person must not miss.
 */

export type AlertTone = "info" | "success" | "warning" | "danger";

export interface AlertProps {
  tone?: AlertTone;
  children: ReactNode;
  /** One tertiary action under the text, left aligned to the text. */
  action?: ReactNode;
  className?: string;
}

/* Stroke glyphs from the library; the mark is never a filled tick or a cross in a circle. */
const MARK: Record<AlertTone, IconName> = {
  info: "lightBulb",
  success: "check-circle",
  warning: "warning",
  danger: "warning",
};

const TONE_CLASS: Record<AlertTone, string> = {
  info: "ds-alert-info",
  success: "ds-alert-success",
  warning: "ds-alert-warning",
  danger: "ds-alert-danger",
};

export function Alert({
  tone = "info",
  children,
  action,
  className,
}: AlertProps) {
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      aria-live={tone === "danger" ? "assertive" : "polite"}
      className={cx("ds-alert", TONE_CLASS[tone], className)}
    >
      <span className="flex flex-none items-center" style={{ height: 21 }}>
        <Icon name={MARK[tone]} size={20} />
      </span>
      <div className="gap-gk-2 flex min-w-0 flex-1 flex-col">
        <div>{children}</div>
        {action && (
          <div className="gap-gk-2 flex flex-wrap items-center">{action}</div>
        )}
      </div>
    </div>
  );
}
